import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  InputAdornment,
  CircularProgress,
  InputLabel,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { ApiUrl } from '../../../../store/config';
import { getContactTags, clearContactTags } from '../../../../store/Workflows';

import ScheduleTriggerConfig from './ScheduleTriggerConfig';
import VariableHighlightInput from './VariableHighlightInput';
import IntegrationSelector from '../../../../components/Integration/IntegrationSelector';

/**
 * Field Component Registry
 * Manages all field type renderers and provides dynamic field rendering
 */
export class FieldComponentRegistry {
  constructor() {
    this.renderers = new Map();
    this.fieldOptionsCache = new Map();
    this.registerBuiltInRenderers();
  }

  register(fieldType, renderer) {
    this.renderers.set(fieldType, renderer);
  }

  get(fieldType) {
    return this.renderers.get(fieldType) || this.renderers.get('default');
  }

  keys() {
    return this.renderers.keys();
  }

  registerBuiltInRenderers() {
    // Register all built-in field renderers
    Object.entries(FieldRenderers).forEach(([type, renderer]) => {
      this.register(type, renderer);
    });

    // Register default fallback renderer
    this.register('default', FieldRenderers.unsupported);
  }
}

/**
 * Tag Manager Component
 * Interactive tag management with add/remove/replace operations
 */
const TagManager = ({ field, value, onChange, config, nodeTemplate, disabled }) => {
  // Redux hooks
  const dispatch = useDispatch();
  const workflowsState = useSelector(state => state.Workflows);

  // Safely extract state with fallbacks
  const contactTags = workflowsState?.contactTags || null;
  const contactTagsLoading = workflowsState?.contactTagsLoading || false;

  // Local state
  const [action, setAction] = useState(value?.action || 'add');

  // Extract current and all tags from Redux state
  const currentTags = contactTags?.tags || [];
  const allTags = contactTags?.allTags || [];
  const loading = contactTagsLoading;

  // Check if contact identifier is a variable reference
  const contactId = config.updateContactId ||
    config.updateContactEmail ||
    config.updateContactPhone ||
    config.contactIdentifier ||
    config.contactEmail ||
    config.contactId ||
    config.contactPhone;
  const isVariableReference = contactId && (contactId.includes('{{') || contactId.includes('}}'));

  // Sync action state with value prop
  useEffect(() => {
    if (value?.action && value.action !== action) {
      setAction(value.action);
    }
  }, [value?.action]);

  // Fetch existing tags from contact with debouncing
  useEffect(() => {
    // Check multiple possible field names for contact identifier
    const contactId = config.updateContactId ||
      config.updateContactEmail ||
      config.updateContactPhone ||
      config.contactIdentifier ||
      config.contactEmail ||
      config.contactId ||
      config.contactPhone;

    // Check if it's a variable reference
    const isVariableReference = contactId && (contactId.includes('{{') || contactId.includes('}}'));

    // Skip if no valid contact ID or if it's a variable reference
    if (!contactId || isVariableReference) {
      return;
    }

    // Debounce: Wait for user to stop typing (800ms delay)
    const timeoutId = setTimeout(() => {
      console.log('[TagManager] Fetching tags for:', contactId);
      dispatch(getContactTags({
        contactId: contactId,
        failCallback: (error) => {
          console.error('[TagManager] Failed to fetch tags:', error);
        }
      }));
    }, 800);

    // Cleanup: Cancel the timeout if contactId changes before 800ms
    return () => {
      clearTimeout(timeoutId);
    };
  }, [contactId, isVariableReference, dispatch]);

  // Cleanup tags on unmount
  useEffect(() => {
    return () => {
      dispatch(clearContactTags());
    };
  }, [dispatch]);

  const handleTagsChange = (event, newTags) => {
    // Format: { action: 'add'|'remove'|'set', tags: [...] }
    onChange(field.name, { action, tags: newTags });
  };

  const handleActionChange = (newAction) => {
    setAction(newAction);
    // Update the value immediately with the new action
    onChange(field.name, { action: newAction, tags: value?.tags || [] });
  };

  const getPlaceholder = () => {
    switch (action) {
      case 'add': return 'Type to add new tags or select from existing...';
      case 'remove': return 'Select tags to remove...';
      case 'set': return 'Type new tags to replace all existing tags...';
      default: return 'Select tags...';
    }
  };

  const getHelperText = () => {
    switch (action) {
      case 'add': return 'Selected tags will be added to the contact (existing tags remain)';
      case 'remove': return 'Selected tags will be removed from the contact';
      case 'set': return 'All existing tags will be replaced with the selected tags';
      default: return 'Select action and tags';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Current Tags Display */}
      {!loading && currentTags.length > 0 && (
        <Box sx={{
          mb: 2,
          p: 2,
          bgcolor: '#F6F7FF50',
          borderRadius: 2,
          border: '1px solid #CAD6E4'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, }}>
              Current Contact Tags
            </Typography>
            <Chip
              label={`${currentTags.length} tag${currentTags.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="filled"
            />
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 1
          }}>
            {currentTags.map((tag, index) => {
              // Handle both string and object tag formats
              const tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.label || String(tag));
              const tagColor = typeof tag === 'object' ? tag?.color : undefined;

              return (
                <Chip
                  key={`current-${tagName}-${index}`}
                  label={tagName}
                  size="medium"
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontWeight: 500,
                    justifyContent: 'flex-start',
                    ...(tagColor && {
                      borderColor: tagColor,
                      color: tagColor
                    })
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {loading && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Loading contact tags...
          </Typography>
        </Box>
      )}

      {!loading && currentTags.length === 0 && !isVariableReference && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">
            This contact has no tags yet
          </Typography>
        </Box>
      )}

      {/* Action Selection */}
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontSize: '13px', mb: 1, display: 'block' }}>
          Tag Action:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label="Add"
            color={action === 'add' ? 'primary' : 'default'}
            onClick={() => handleActionChange('add')}
            disabled={disabled}
            size="medium"
            sx={{
              cursor: 'pointer',
              fontWeight: action === 'add' ? 600 : 400
            }}
          />
          <Chip
            label="Remove"
            color={action === 'remove' ? 'error' : 'default'}
            onClick={() => handleActionChange('remove')}
            disabled={disabled}
            size="medium"
            sx={{
              cursor: 'pointer',
              fontWeight: action === 'remove' ? 600 : 400
            }}
          />
          <Chip
            label="Replace All"
            color={action === 'set' ? 'warning' : 'default'}
            onClick={() => handleActionChange('set')}
            disabled={disabled}
            size="medium"
            sx={{
              cursor: 'pointer',
              fontWeight: action === 'set' ? 600 : 400
            }}
          />
        </Box>
      </FormControl>

      {/* Tag Selection Autocomplete */}
      <div className='group-form'>
        <label>{action === 'add' ? 'Add Tags' : action === 'remove' ? 'Remove Tags' : 'New Tags'}</label>
        <Autocomplete
          multiple
          freeSolo={action !== 'remove' || (action === 'remove' && currentTags.length === 0)}
          options={action === 'remove' && currentTags.length > 0 ? currentTags.map(tag => typeof tag === 'string' ? tag : (tag?.name || String(tag))) : allTags}
          value={value?.tags || []}
          onChange={handleTagsChange}
          disabled={disabled || loading}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagName = typeof option === 'string' ? option : (option?.name || String(option));
              return (
                <Chip
                  label={tagName}
                  {...getTagProps({ index })}
                  size="small"
                  color={action === 'remove' ? 'error' : action === 'set' ? 'warning' : 'primary'}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              // placeholder={action === 'add' ? 'Add Tags' : action === 'remove' ? 'Remove Tags' : 'New Tags'}
              placeholder={getPlaceholder()}
              helperText={getHelperText()}
            />
          )}
          renderOption={(props, option) => {
            const tagName = typeof option === 'string' ? option : (option?.name || String(option));
            return (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={tagName}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </li>
            );
          }}
        />
      </div>
    </Box>
  );
};

/**
 * Dynamic Field Options Provider
 * Fetches and caches field options from API endpoints
 */
export class FieldOptionsProvider {
  constructor() {
    this.cache = new Map();
    this.loading = new Map();
  }

  getCacheKey(endpoint, params) {
    return `${endpoint}-${JSON.stringify(params)}`;
  }

  async fetchFieldOptions(endpoint, params = {}, config = {}) {
    // Replace placeholders in endpoint
    let finalEndpoint = endpoint;
    Object.entries(params).forEach(([key, value]) => {
      finalEndpoint = finalEndpoint.replace(`{${key}}`, value);
    });

    const cacheKey = this.getCacheKey(finalEndpoint, params);

    // Return cached if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return null if already loading
    if (this.loading.get(cacheKey)) {
      return null;
    }

    this.loading.set(cacheKey, true);

    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...config.headers
      };

      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `bearer ${token}`;
      }

      const response = await fetch(`${ApiUrl}${finalEndpoint}`, {
        method: config.method || 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch field options: ${response.statusText}`);
      }

      let data = await response.json();

      // Extract data using dataPath if specified
      if (config.dataPath) {
        let finalDataPath = config.dataPath;

        // Handle template substitution in dataPath (e.g., {entity} -> actual entity value)
        if (finalDataPath.includes('{') && finalDataPath.includes('}')) {
          // Extract all template variables
          const templateMatches = finalDataPath.match(/\{([^}]+)\}/g);
          if (templateMatches) {
            templateMatches.forEach(match => {
              const key = match.slice(1, -1); // Remove { and }
              if (params[key]) {
                finalDataPath = finalDataPath.replace(match, params[key]);
              }
            });
          }
        }

        // Check for dataPathOverrides based on config values
        if (config.dataPathOverrides) {
          Object.entries(config.dataPathOverrides).forEach(([configValue, overridePath]) => {
            // Check if any of the params match the override condition
            if (Object.values(params).includes(configValue)) {
              finalDataPath = overridePath;
            }
          });
        }

        const pathParts = finalDataPath.split('.');
        let extractedData = data;

        for (const part of pathParts) {
          if (extractedData && extractedData[part] !== undefined) {
            extractedData = extractedData[part];
          } else {
            extractedData = null;
            break;
          }
        }

        data = extractedData;
      }

      const options = this.transformOptions(data, config);

      this.cache.set(cacheKey, options);
      return options;
    } catch (error) {
      console.error('Error fetching field options:', error);
      return [];
    } finally {
      this.loading.delete(cacheKey);
    }
  }

  transformOptions(data, config) {
    if (!config || !data) {
      return [];
    }

    const transform = config.transform || {};
    const { valueField = 'value', labelField = 'label' } = transform;

    // Handle flatten option for nested object structures
    if (config.flatten && typeof data === 'object' && !Array.isArray(data)) {
      const flattened = [];
      Object.values(data).forEach(group => {
        if (Array.isArray(group)) {
          flattened.push(...group);
        }
      });
      data = flattened;
    }

    if (Array.isArray(data)) {
      return data.map(item => ({
        value: item[valueField] || item.value || item,
        label: item[labelField] || item.label || item.name || item
      }));
    }

    return [];
  }

  clearCache(endpoint) {
    if (endpoint) {
      // Clear specific endpoint cache
      this.cache.forEach((_, key) => {
        if (key.startsWith(endpoint)) {
          this.cache.delete(key);
        }
      });
    } else {
      // Clear all cache
      this.cache.clear();
    }
  }
}

// Singleton instance
export const fieldOptionsProvider = new FieldOptionsProvider();

/**
 * Built-in Field Renderers
 */
export const FieldRenderers = {
  // Text field renderer
  text: ({ field, value, onChange, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <VariableHighlightInput
        fullWidth
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
        disabled={disabled}
        helperText={field.description}
        size="small"
      />
    </div>
  ),

  // String field (alias for text)
  string: ({ field, value, onChange, disabled }) =>
    FieldRenderers.text({ field, value, onChange, disabled }),

  // Number field renderer
  number: ({ field, value, onChange, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <TextField
        fullWidth
        type="number"
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value ? Number(e.target.value) : '')}
        placeholder={field.placeholder}
        required={field.required}
        disabled={disabled}
        helperText={field.description}
        size="small"
      />
    </div>
  ),

  // Select field renderer with dynamic options
  select: ({ field, value, onChange, config, nodeTemplate, dynamicFieldOptions, fetchFieldOptions, disabled }) => {
    const [options, setOptions] = useState(field.options || []);
    const [loading, setLoading] = useState(false);

    // Check if field has dynamic options configuration
    // First try fullPath (for array fields), then fall back to name
    const dynamicConfig = nodeTemplate?.nodeDefinition?.fieldConfigurations?.dynamicOptions?.[field.fullPath] ||
      nodeTemplate?.nodeDefinition?.fieldConfigurations?.dynamicOptions?.[field.name];

    useEffect(() => {
      if (dynamicConfig && fetchFieldOptions) {
        setLoading(true);
        const params = {};

        // Collect dependent field values
        if (dynamicConfig.dependsOn) {
          dynamicConfig.dependsOn.forEach(dep => {
            params[dep] = config[dep];
          });
        }

        fetchFieldOptions(dynamicConfig.endpoint, params, dynamicConfig)
          .then(fetchedOptions => {
            if (fetchedOptions) {
              // Merge fetched options with additional options if specified
              const allOptions = dynamicConfig.additionalOptions
                ? [...fetchedOptions, ...dynamicConfig.additionalOptions]
                : fetchedOptions;
              setOptions(allOptions);
            }
          })
          .finally(() => setLoading(false));
      }
    }, [dynamicConfig, config, field.name, fetchFieldOptions]);

    return (
      <div key={field.name} className='group-form'>
        <label>{field.label}</label>
        <FormControl fullWidth>
          <Select
            value={value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
            disabled={disabled || loading}
            displayEmpty
            size="small"
          >
            <MenuItem value="">
              <em>{loading ? 'Loading...' : `Select ${field.label}`}</em>
            </MenuItem>
            {options.map(option => (
              <MenuItem
                key={typeof option === 'object' ? option.value : option}
                value={typeof option === 'object' ? option.value : option}
              >
                {typeof option === 'object' ? option.label : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {field.description && (
          <Typography variant="caption" color="textSecondary">
            {field.description}
          </Typography>
        )}
      </div>
    );
  },

  // Boolean field renderer
  boolean: ({ field, value, onChange, disabled }) => (
    <div key={field.name} className='group-form'>
      <FormControlLabel
        control={
          <Checkbox
            checked={value || false}
            onChange={(e) => onChange(field.name, e.target.checked)}
            disabled={disabled}
          />
        }
        label={field.label}
      />
      {field.description && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
          {field.description}
        </Typography>
      )}
    </div>
  ),

  // JSON/Object field renderer
  json: ({ field, value, onChange, disabled }) => {
    const [jsonError, setJsonError] = React.useState('');
    const [jsonValue, setJsonValue] = React.useState(
      typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)
    );

    // Sync internal state when value prop changes (when node is reopened)
    React.useEffect(() => {
      const newJsonValue = typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2);
      setJsonValue(newJsonValue);
      // Clear error when loading saved value
      setJsonError('');
    }, [value]);

    const handleJsonChange = (e) => {
      const newValue = e.target.value;
      console.log(`[JSON Field - ${field.name}] User typed:`, newValue);
      setJsonValue(newValue);

      try {
        // Validate JSON syntax
        const parsed = JSON.parse(newValue);
        setJsonError('');
        onChange(field.name, parsed);
      } catch (error) {
        setJsonError('Invalid JSON');
        console.log(`[JSON Field - ${field.name}] ❌ Invalid JSON:`, error.message);
      }
    };

    return (
      <div key={field.name} className='group-form'>
        <label>{field.label}</label>
        <TextField
          multiline
          rows={6}
          fullWidth
          value={jsonValue}
          onChange={handleJsonChange}
          placeholder={field.placeholder || '{}'}
          disabled={disabled}
          error={!!jsonError}
          helperText={jsonError || field.description}
          variant="outlined"
          size="small"
          sx={{
            fontFamily: 'monospace',
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }
          }}
        />
        {/* Show example if provided */}
        {field.placeholder && (
          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', cursor: 'pointer' }}
            onClick={() => {
              setJsonValue(field.placeholder);
              try {
                const parsed = JSON.parse(field.placeholder);
                setJsonError('');
                onChange(field.name, parsed);
              } catch (e) {
                // ignore
              }
            }}
          >
            Click to use example
          </Typography>
        )}
      </div>
    );
  },

  // Object field (alias for json)
  object: ({ field, value, onChange, disabled }) =>
    FieldRenderers.json({ field, value, onChange, disabled }),

  // Array field renderer
  array: ({ field, value, onChange, config, nodeTemplate, dynamicFieldOptions, fetchFieldOptions, disabled }) => {
    const arrayValue = Array.isArray(value) ? value : [];

    const getDefaultItem = () => {
      if (field.itemType === 'object' && field.itemSchema) {
        return Object.keys(field.itemSchema).reduce((acc, key) => {
          acc[key] = field.itemSchema[key].defaultValue || '';
          return acc;
        }, {});
      }
      return '';
    };

    const addItem = () => {
      onChange(field.name, [...arrayValue, getDefaultItem()]);
    };

    const removeItem = (index) => {
      onChange(field.name, arrayValue.filter((_, i) => i !== index));
    };

    const updateItem = (index, itemValue) => {
      const newArray = [...arrayValue];
      newArray[index] = itemValue;
      onChange(field.name, newArray);
    };

    return (
      <div key={field.name} className='group-form'>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">{field.label}</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addItem}
            disabled={disabled}
          >
            Add {field.label}
          </Button>
        </Box>

        {arrayValue.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
            {field.itemType === 'object' && field.itemSchema ? (
              <Box sx={{ flex: 1 }}>
                {Object.entries(field.itemSchema).map(([subFieldName, subField]) => {
                  // Check if field should be visible based on conditions
                  if (subField.condition) {
                    let shouldShow = true;
                    for (const [condField, condValue] of Object.entries(subField.condition)) {
                      if (Array.isArray(condValue)) {
                        if (!condValue.includes(item[condField])) {
                          shouldShow = false;
                          break;
                        }
                      } else if (item[condField] !== condValue) {
                        shouldShow = false;
                        break;
                      }
                    }
                    if (!shouldShow) return null;
                  }

                  // Render sub-fields based on their type
                  let fieldType = subField.type;

                  // Check for dynamic field type overrides based on parent field value
                  const parentFieldName = field.name; // e.g., "updateFields"
                  const dynamicConfig = nodeTemplate?.nodeDefinition?.fieldConfigurations?.dynamicOptions?.[`${parentFieldName}.${subFieldName}`];
                  if (dynamicConfig?.fieldTypeOverrides) {
                    // Check if current item has a field value that triggers override
                    const triggerFieldValue = item['field']; // e.g., "tags"
                    if (triggerFieldValue && dynamicConfig.fieldTypeOverrides[triggerFieldValue]) {
                      fieldType = dynamicConfig.fieldTypeOverrides[triggerFieldValue];
                    }
                  }

                  const SubFieldRenderer = FieldRenderers[fieldType] || FieldRenderers.text;
                  return (
                    <Box key={subFieldName} sx={{ mb: 1 }}>
                      <SubFieldRenderer
                        field={{ ...subField, name: subFieldName, fullPath: `${field.name}.${subFieldName}` }}
                        value={item[subFieldName]}
                        onChange={(_, val) => updateItem(index, { ...item, [subFieldName]: val })}
                        config={config}  // Pass the main config to have access to entity and other top-level fields
                        nodeTemplate={nodeTemplate}
                        dynamicFieldOptions={dynamicFieldOptions}
                        fetchFieldOptions={fetchFieldOptions}
                        disabled={disabled}
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <VariableHighlightInput
                size="small"
                fullWidth
                value={item}
                onChange={(e) => updateItem(index, e.target.value)}
                disabled={disabled}
              />
            )}
            <IconButton
              size="small"
              onClick={() => removeItem(index)}
              disabled={disabled}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        {field.description && (
          <Typography variant="caption" color="textSecondary">
            {field.description}
          </Typography>
        )}
      </div>
    );
  },

  // Connection status field renderer (deprecated - connection UI moved to IntegrationSelector dropdown)
  'connection-status': () => null,

  // Agent select field renderer
  'agent-select': ({ field, value, onChange, allAgents, allAgentsLoading, onAddAgent, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <FormControl fullWidth>
        <Select
          value={value || ''}
          onChange={(e) => onChange(field.name, e.target.value)}
          required={field.required}
          disabled={disabled || allAgentsLoading}
          displayEmpty
          size="small"
        >
          <MenuItem value="">
            <em>Select Agent</em>
          </MenuItem>
          {allAgents?.map(agent => (
            <MenuItem key={agent._id} value={agent._id}>
              {agent.name} ({agent.mode || 'voice'})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {allAgentsLoading && <CircularProgress size={20} />}
      {onAddAgent && (
        <Button
          size="small"
          onClick={onAddAgent}
          sx={{ mt: 1 }}
        >
          + Create New Agent
        </Button>
      )}
      {field.description && (
        <Typography variant="caption" color="textSecondary">
          {field.description}
        </Typography>
      )}
    </div>
  ),

  // Phone select field renderer
  'phone-select': ({ field, value, onChange, allPhones, phonesLoading, onAddPhone, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <FormControl fullWidth>
        <Select
          value={value || ''}
          onChange={(e) => onChange(field.name, e.target.value)}
          required={field.required}
          disabled={disabled || phonesLoading}
          displayEmpty
          size="small"
        >
          <MenuItem value="">
            <em>Select Phone Number</em>
          </MenuItem>
          {allPhones?.map(phone => (
            <MenuItem key={phone._id} value={phone._id}>
              {phone.displayName || phone.phoneNumber}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {phonesLoading && <CircularProgress size={20} />}
      {onAddPhone && (
        <Button
          size="small"
          onClick={onAddPhone}
          sx={{ mt: 1 }}
        >
          + Add New Phone
        </Button>
      )}
      {field.description && (
        <Typography variant="caption" color="textSecondary">
          {field.description}
        </Typography>
      )}
    </div>
  ),

  // Schedule config field renderer
  'schedule-config': ({ field, value, onChange, disabled }) => (
    <div key={field.name} className='group-form'>
      <ScheduleTriggerConfig
        value={value}
        onChange={(newSchedule) => onChange(field.name, newSchedule)}
        disabled={disabled}
      />
      {field.description && (
        <Typography variant="caption" color="textSecondary">
          {field.description}
        </Typography>
      )}
    </div>
  ),

  // Webhook URL field renderer
  'webhook-url': ({ field, value, onChange, workflowId, nodeId, nodeTemplate, disabled }) => {
    let webhookUrl = value;

    // Debug logging
    React.useEffect(() => {
      console.log('[webhook-url renderer] Props:', {
        fieldName: field.name,
        value,
        workflowId,
        nodeId,
        urlPattern: field.urlPattern,
        disabled
      });
    }, [field.name, value, workflowId, nodeId, field.urlPattern, disabled]);

    // Generate webhook URL if not already set
    React.useEffect(() => {
      if (!value && workflowId && field.urlPattern && nodeId) {
        const baseUrl = ApiUrl.replace('/api', '');
        const generatedUrl = field.urlPattern
          .replace('{baseUrl}', baseUrl)
          .replace('{workflowId}', workflowId)
          .replace('{nodeId}', nodeId);
        console.log('[webhook-url renderer] Generating URL:', generatedUrl);
        // Update the field value with generated URL
        onChange(field.name, generatedUrl);
      }
    }, [workflowId, nodeId, field.urlPattern, field.name, onChange, value]);

    if (!webhookUrl && workflowId && field.urlPattern && nodeId) {
      const baseUrl = ApiUrl.replace('/api', '');
      webhookUrl = field.urlPattern
        .replace('{baseUrl}', baseUrl)
        .replace('{workflowId}', workflowId)
        .replace('{nodeId}', nodeId);
    }

    return (
      <div key={field.name} className='group-form copy-group-workflow'>
        <label>{field.label || 'Webhook URL'}</label>
        <TextField
          fullWidth
          value={webhookUrl || (workflowId && nodeId ? 'Loading webhook URL...' : 'Save the workflow first to generate webhook URL')}
          InputProps={{
            readOnly: true,
            endAdornment: webhookUrl && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(webhookUrl);
                    // Get toast message from node definition if available
                    const toastMsg = nodeTemplate?.uiMessages?.toasts?.webhookUrlCopied ||
                      nodeTemplate?.nodeDefinition?.uiMessages?.toasts?.webhookUrlCopied ||
                      'Webhook URL copied to clipboard';
                    toast.success(toastMsg);
                  }}
                  edge="end"
                >
                  <ContentCopy />
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={field.description || 'POST data to this URL to trigger the workflow'}
          disabled={disabled}
          size="small"
        />
      </div>
    );
  },

  // Multi-line editor / textarea renderer
  'multi-line-editor': ({ field, value, onChange, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <VariableHighlightInput
        fullWidth
        multiline
        rows={field.rows || 4}
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
        helperText={field.description}
        disabled={disabled}
        size="small"
        InputProps={field.showVariableHelper ? {
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" color="textSecondary">
                Use {'{{variableName}}'} to reference other nodes
              </Typography>
            </InputAdornment>
          ),
        } : {}}
      />
    </div>
  ),

  // Textarea (alias for multi-line-editor)
  textarea: ({ field, value, onChange, disabled }) =>
    FieldRenderers['multi-line-editor']({ field, value, onChange, disabled }),

  // Model select field renderer
  'model-select': ({ field, value, onChange, disabled }) => {
    // Models should always be provided in field configuration from node definition
    const models = field.models || [];

    return (
      <div key={field.name} className='group-form'>
        <label>{field.label}</label>
        <FormControl fullWidth>
          <Select
            value={value || models[0]?.value || ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            required={field.required}
            disabled={disabled}
            size="small"
          >
            {models.map(model => (
              <MenuItem key={model.value} value={model.value}>
                {model.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {field.description && (
          <Typography variant="caption" color="textSecondary">
            {field.description}
          </Typography>
        )}
      </div>
    );
  },

  // Conditions builder field renderer
  'conditions-builder': ({ field, value, onChange, nodes, nodeId, nodeTemplate, disabled }) => {
    // Handle both single condition (object) and multiple conditions (array)
    const isSingleCondition = value && !Array.isArray(value) && typeof value === 'object';
    const conditions = isSingleCondition ? [value] : (Array.isArray(value) ? value : []);

    // Get operators from node definition
    const operators = nodeTemplate?.nodeDefinition?.fieldConfigurations?.operators ||
      nodeTemplate?.fieldConfigurations?.operators || [];

    const addCondition = () => {
      if (isSingleCondition) return; // Don't allow adding for single condition

      const newCondition = {
        id: `condition-${Date.now()}`,
        name: `Condition ${conditions.length + 1}`,
        source: '',
        operator: 'equals',
        value: '',
        targetNodeId: ''
      };
      onChange(field.name, [...conditions, newCondition]);
    };

    const updateCondition = (index, updates) => {
      if (isSingleCondition) {
        // For single condition, update the object directly
        onChange(field.name, { ...conditions[0], ...updates });
      } else {
        // For multiple conditions, update the array
        const newConditions = [...conditions];
        newConditions[index] = { ...newConditions[index], ...updates };
        onChange(field.name, newConditions);
      }
    };

    const removeCondition = (index) => {
      if (isSingleCondition) return; // Don't allow removing for single condition
      onChange(field.name, conditions.filter((_, i) => i !== index));
    };

    return (
      <div key={field.name} className='group-form'>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2">{field.label}</Typography>
          {!isSingleCondition && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addCondition}
              disabled={disabled}
            >
              Add IF Condition
            </Button>
          )}
        </Box>

        {conditions.map((condition, index) => (
          <Box key={condition.id || index} sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            mb: 2,
            backgroundColor: '#fafafa'
          }}>
            <Box className="veriable-inputs">
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <label>Variable</label>
                <VariableHighlightInput
                  size="small"
                  value={condition.source || ''}
                  onChange={(e) => updateCondition(index, { source: e.target.value })}
                  placeholder="{{nodeName.output}}"
                  disabled={disabled}
                />
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <label>Operator</label>
                <Select
                  value={condition.operator || 'equals'}
                  onChange={(e) => updateCondition(index, { operator: e.target.value })}
                  label="Operator"
                  disabled={disabled}
                >
                  {operators.length > 0 ? (
                    operators.map(op => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))
                  ) : (
                    // Fallback if no operators defined
                    <MenuItem value="equals">Equals</MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Only show Value field for operators that require a value */}
              {!['exists', 'notExists', 'isEmpty', 'isNotEmpty'].includes(condition.operator) && (
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <label>Value</label>
                  <VariableHighlightInput
                    size="small"
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                    placeholder="Value to compare"
                    disabled={disabled}
                    sx={{ flex: 1, minWidth: 150 }}
                  />
                </FormControl>
              )}

              {!isSingleCondition && (
                <>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium', mx: 1 }}>
                    THEN GO TO
                  </Typography>

                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <label>Target Node</label>
                    <Select
                      value={condition.targetNodeId || ''}
                      onChange={(e) => updateCondition(index, { targetNodeId: e.target.value })}
                      label="Target Node"
                      disabled={disabled}
                    >
                      <MenuItem value="">
                        <em>Select target node</em>
                      </MenuItem>
                      {nodes.filter(n => n.id !== nodeId).map(node => (
                        <MenuItem key={node.id} value={node.id}>
                          {node.data?.label || node.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {!isSingleCondition && (
                <IconButton
                  size="small"
                  onClick={() => removeCondition(index)}
                  disabled={disabled}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}

        {conditions.length === 0 && (
          <Alert severity="info">
            No IF conditions defined. Click "Add IF Condition" to create your first condition.
          </Alert>
        )}

        {field.description && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            {field.description}
          </Typography>
        )}
      </div>
    );
  },

  // Variable reference field renderer
  'variable-reference': ({ field, value, onChange, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <VariableHighlightInput
        fullWidth
        value={value || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        placeholder={field.placeholder || 'e.g., {{nodeName.output.field}}'}
        required={field.required}
        helperText={field.description || 'Use {{variableName}} syntax to reference data from other nodes'}
        disabled={disabled}
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="caption" color="primary" sx={{ cursor: 'help' }}>
                {'{{}}'}
              </Typography>
            </InputAdornment>
          ),
        }}
      />
    </div>
  ),

  // Action button field renderer
  'action-button': ({ field, value, onChange, config, workflowId, nodeTemplate, disabled }) => (
    <div key={field.name} className='group-form'>
      <Button
        fullWidth
        variant={field.variant || 'contained'}
        color={field.color || 'primary'}
        onClick={() => {
          // Handle different action types
          if (field.action === 'test-webhook' && config.triggerType === 'webhook') {
            const baseUrl = ApiUrl.replace('/api', '');
            const webhookUrl = field.urlPattern
              ? field.urlPattern
                .replace('{baseUrl}', baseUrl)
                .replace('{workflowId}', workflowId)
              : '';

            if (webhookUrl) {
              // Get test configuration from node definition
              const testConfig = nodeTemplate?.nodeDefinition?.testConfig?.webhook ||
                nodeTemplate?.testConfig?.webhook || {};

              const messages = testConfig.messages || {};
              const authHeaders = testConfig.authHeaders || {};

              // Build headers based on authentication type
              const headers = { 'Content-Type': 'application/json' };
              if (config.authentication && config.authToken && authHeaders[config.authentication]) {
                const authConfig = authHeaders[config.authentication];
                headers[authConfig.header] = authConfig.prefix
                  ? `${authConfig.prefix} ${config.authToken}`
                  : config.authToken;
              }

              // Build test payload
              const payload = testConfig.defaultPayload || {
                test: true,
                timestamp: new Date().toISOString(),
                message: 'Test webhook from workflow builder'
              };

              // Replace timestamp placeholder
              const processedPayload = JSON.parse(
                JSON.stringify(payload).replace('{{timestamp}}', new Date().toISOString())
              );

              toast.info(messages.testing || 'Testing webhook...');
              fetch(webhookUrl, {
                method: testConfig.method || 'POST',
                headers,
                body: JSON.stringify(processedPayload)
              })
                .then(response => {
                  if (response.ok) {
                    toast.success(messages.success || 'Webhook test successful!');
                  } else {
                    const failedMsg = (messages.failed || 'Webhook test failed: {{status}}')
                      .replace('{{status}}', response.status);
                    toast.error(failedMsg);
                  }
                })
                .catch(error => {
                  const errorMsg = (messages.error || 'Webhook test failed: {{message}}')
                    .replace('{{message}}', error.message);
                  toast.error(errorMsg);
                });
            } else {
              toast.error(messages.noUrl || 'Please save the workflow first to generate webhook URL');
            }
          } else if (field.action) {
            // Generic action handler
            toast.info(`Action: ${field.action}`);
          }
        }}
        disabled={disabled || (field.action === 'test-webhook' && !workflowId)}
        startIcon={field.action === 'test' ? <PlayIcon /> : undefined}
      >
        {field.buttonText || field.label}
      </Button>
      {field.description && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          {field.description}
        </Typography>
      )}
    </div>
  ),

  // Tag manager field renderer
  'tag-manager': ({ field, value, onChange, config, nodeTemplate, disabled }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <TagManager
        field={field}
        value={value}
        onChange={onChange}
        config={config}
        nodeTemplate={nodeTemplate}
        disabled={disabled}
      />
    </div>
  ),

  // Integration selector field renderer
  'integration-selector': ({ field, value, onChange, disabled, onConnectNew }) => (
    <div key={field.name} className='group-form'>
      <label>{field.label}</label>
      <IntegrationSelector
        toolName={field.toolName} // e.g., "Gmail", "Twilio SMS", "WhatsApp"
        value={value}
        onChange={(integrationId) => onChange(field.name, integrationId)}
        disabled={disabled}
        placeholder={field.placeholder || `Select ${field.toolName} account`}
        onConnectNew={onConnectNew}
      />
      {field.description && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
          {field.description}
        </Typography>
      )}
    </div>
  ),

  // Default unsupported field renderer
  unsupported: ({ field }) => (
    <div key={field.name} className='group-form'>
      <Alert severity="warning">
        Unsupported field type: {field.type} for field {field.name}
      </Alert>
    </div>
  )
};

// Create and export singleton instance
export const fieldComponentRegistry = new FieldComponentRegistry();